import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { checkUserExists } from '@/lib/utils/auth-error-handling';
import { useToast } from '@/hooks/use-toast';

interface UseOAuthSignupOptions {
  provider: 'google' | 'github' | 'discord';
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export function useOAuthSignup(options: UseOAuthSignupOptions) {
  const [loading, setLoading] = useState(false);
  const [showExistingAccountDialog, setShowExistingAccountDialog] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const signUpWithOAuth = useCallback(async () => {
    try {
      setLoading(true);
      
      // Set a flag to indicate this is an OAuth flow
      sessionStorage.setItem('oauth_flow', 'true');
      
      // Early check for existing user before initiating OAuth
      const email = await getEmailFromOAuthProvider(options.provider);
      if (email) {
        const userExists = await checkUserExists(email);
        if (userExists) {
          setShowExistingAccountDialog(true);
          return;
        }
      }
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: options.provider,
        options: {
          redirectTo: `${window.location.origin}/onboarding`,
          queryParams: { prompt: 'select_account' }
        }
      });

      if (error) {
        if (options.onError) {
          options.onError(error);
        } else {
          toast({
            title: 'Authentication Error',
            description: error.message || 'Failed to sign in with OAuth',
            variant: 'destructive'
          });
        }
        return;
      }

      if (options.onSuccess) {
        options.onSuccess();
      }
    } catch (error) {
      if (options.onError) {
        options.onError(error);
      } else {
        toast({
          title: 'Authentication Error',
          description: 'An unexpected error occurred during OAuth signup',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [options, navigate, toast]);

  const handleExistingAccountSignIn = useCallback(() => {
    setShowExistingAccountDialog(false);
    navigate('/login');
  }, [navigate]);

  const closeDialog = useCallback(() => {
    setShowExistingAccountDialog(false);
  }, []);

  return {
    signUpWithOAuth,
    loading,
    showExistingAccountDialog,
    handleExistingAccountSignIn,
    closeDialog
  };
}

// Helper function to try to get email from OAuth provider
async function getEmailFromOAuthProvider(provider: string): Promise<string | null> {
  try {
    // For Google, we can try to get the email from the current session or stored data
    if (provider === 'google') {
      // Check if there's already a Google account signed in
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        return session.user.email;
      }
      
      // Check localStorage for any stored OAuth data
      const storedEmail = localStorage.getItem('oauth_email');
      if (storedEmail) {
        return storedEmail;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting email from OAuth provider:', error);
    return null;
  }
}
